/**
 * Verified Users Configuration
 *
 * This file maintains the list of verified users on TTStats.
 * Users can be verified as either 'staff' (team members) or 'pro' (verified pro users).
 */

export interface VerifiedUser {
  // Unique identifier - can be player URL, name, or license number
  identifier: string;
  type: 'staff' | 'pro';
  // Optional: match type (exact, contains, starts_with, ends_with)
  matchType?: 'exact' | 'contains' | 'starts_with' | 'ends_with';
}

export const verifiedUsers: VerifiedUser[] = [
  // TTStats Team Members
  {
    identifier: 'Oztürk, Doruk Tan',
    type: 'staff',
    matchType: 'exact' // Exact match for full name
  },
  {
    identifier: 'Cem Cüneyt',
    type: 'pro',
    matchType: 'contains' // Matches any player name containing "Cüneyt"
  },

  // TTStats Pro Users
  // Add verified pro users here, e.g.:
  // {
  //   identifier: 'player-name',
  //   type: 'pro',
  //   matchType: 'exact'
  // },
];

/**
 * Check if a user is verified and return their verification type
 * @param playerName - The player's name
 * @param playerUrl - The player's URL (optional)
 * @param license - The player's license number (optional)
 * @returns The verification type ('staff' or 'pro') or null if not verified
 */
export const getVerificationStatus = (
  playerName?: string,
  playerUrl?: string,
  license?: string
): 'staff' | 'pro' | null => {
  if (!playerName && !playerUrl && !license) return null;

  for (const user of verifiedUsers) {
    const { identifier, type, matchType = 'exact' } = user;
    const identifierLower = identifier.toLowerCase();

    // Check player name
    if (playerName) {
      const nameLower = playerName.toLowerCase();
      if (matchType === 'exact' && nameLower === identifierLower) return type;
      if (matchType === 'contains' && nameLower.includes(identifierLower)) return type;
      if (matchType === 'starts_with' && nameLower.startsWith(identifierLower)) return type;
      if (matchType === 'ends_with' && nameLower.endsWith(identifierLower)) return type;
    }

    // Check player URL
    if (playerUrl) {
      const urlLower = playerUrl.toLowerCase();
      if (matchType === 'exact' && urlLower === identifierLower) return type;
      if (matchType === 'contains' && urlLower.includes(identifierLower)) return type;
      if (matchType === 'starts_with' && urlLower.startsWith(identifierLower)) return type;
      if (matchType === 'ends_with' && urlLower.endsWith(identifierLower)) return type;
    }

    // Check license number
    if (license) {
      const licenseLower = license.toLowerCase();
      if (matchType === 'exact' && licenseLower === identifierLower) return type;
      if (matchType === 'contains' && licenseLower.includes(identifierLower)) return type;
      if (matchType === 'starts_with' && licenseLower.startsWith(identifierLower)) return type;
      if (matchType === 'ends_with' && licenseLower.endsWith(identifierLower)) return type;
    }
  }

  return null;
};
